import { useDebounce, useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  Anchor,
  Button,
  Dialog,
  DialogPlugin,
  Divider,
  Input,
  Link,
  MessagePlugin,
  Radio,
  Select,
  Space,
  Switch,
  Textarea,
} from 'tdesign-react';
import { useNavigate } from 'react-router-dom';
import formRulePattern from '@/utils/formRulePattern';
import {
  addCluster,
  deleteCluster,
  getClusterInfo,
  updateCluster,
  validateBookieAdmin,
  validateGrafana,
  validateKop,
  validatePrometheus,
  validatePulsarAdmin,
  validatePulsarClient,
} from '@/services/kopClusterService';
import { isVoid, toTsOptions } from '@/utils/common';
import ColTip from '@/components/Tip';
import ValidateWrapper from '@/components/Form/ValidateWrapper';
import Form from '@/components/Form';
import DisableWrapper from '@/components/DisableWrapper';
import { fetchClusterAdminList } from '@/slices/kopClusterSlice';
import SelectCollapsed from '@/components/Form/SelectCollapsed';

const { AnchorItem } = Anchor;
const { FormItem } = Form;

const initial = {
  clusterAuthType: 'JWT',
  isOpenGrafanaService: true,
  clusterType: 'kop',
};

function LinkClusterModal({
  clusterEnvs,
  clusterUsage,
  ZKVersions,
  kafkaVersions,
  BKVersions,
  deployVersions,
  deployEnvs,
  visible,
  handleClose,
  clusterName,
  adminOptions,
  fetchClusterAdminList,
  ...others
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const clusterAuthType = Form.useWatch('clusterAuthType', form);
  const clusterEnv = Form.useWatch('clusterEnv', form);
  const jwt = Form.useWatch('jwt', form);
  const username = Form.useWatch('username', form);
  const password = Form.useWatch('password', form);
  const isOpenGrafanaService = Form.useWatch('isOpenGrafanaService', form);
  const grafanaAuthUser = Form.useWatch('grafanaAuthUser', form);
  const grafanaAuthPassword = Form.useWatch('grafanaAuthPassword', form);
  const clusterType = Form.useWatch('clusterType', form);
  // anchor 细微bug，临时方案
  const debouceVisible = useDebounce(visible, { wait: 100 });

  const doInit = useMemoizedFn(async () => {
    fetchClusterAdminList();
    if (clusterName) {
      const { data } = await getClusterInfo(clusterName);
      const values = {
        ...initial,
        ...data,
        principal: (data.principal || '').split(','),
      };
      if (data.clusterAuthType === 'JWT') {
        const json = JSON.parse(data.clusterAuthParams);
        values.jwt = json?.token || '';
      }
      if (data.clusterAuthType === 'SCRAM') {
        const json = JSON.parse(data.clusterAuthParams);
        values.username = json?.username || '';
        values.password = json?.password || '';
      }

      form.setFieldsValue(values);
    }
  });

  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      form.resetFields();
    }
  }, [visible, form, doInit, clusterName]);

  const handleClick = ({ e }) => {
    e.preventDefault();
  };

  const handleSubmit = async (fields) => {
    const service = clusterName ? updateCluster : addCluster;

    const values = {
      ...fields,
      description: fields.description || '', // 补充空字符串
      principal: (fields.principal || []).join(','),
    };

    if (fields.clusterAuthType === 'JWT') {
      values.clusterAuthParams = JSON.stringify({
        token: fields.jwt,
      });
    }

    if (fields.clusterAuthType === 'SCRAM') {
      values.clusterAuthParams = JSON.stringify({
        username: fields.username,
        password: fields.password,
      });
    }

    try {
      setSubmitting(true);
      const { data } = await service(values);
      if (data && data.err) {
        MessagePlugin.warning(data.err);
      } else {
        MessagePlugin.success('操作成功！');
        handleClose(true);
      }
    } catch (error) {
      MessagePlugin.warning('操作失败！');
    } finally {
      setSubmitting(false);
    }
  };

  const validateEnv = () => {
    if (isVoid(clusterEnv)) {
      MessagePlugin.warning('请先选择部署环境');
      throw new Error('请先选择部署环境');
    }
  };

  const clusterTypeChange = (type) => {
    if (type === 'kop') {
      form.setFieldsValue({ clusterAuthType: 'JWT' });
    }
    if (type === 'kafka') {
      form.setFieldsValue({ clusterAuthType: 'SCRAM' });
    }
  };

  const confirmDelete = async () => {
    const dialog = DialogPlugin.confirm({
      header: '确定删除？',
      theme: 'warning',
      body: '确定删除集群？',
      onClose: () => dialog.hide(),
      onConfirm: async () => {
        try {
          dialog.update({
            confirmBtn: { loading: true, content: '确定' },
          });
          await deleteCluster(clusterName);
          MessagePlugin.success('操作成功！');
          dialog.hide();
          navigate('/kop-cluster');
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { loading: false, content: '确定' },
          });
        }
      },
    });
  };

  return (
    <Dialog
      header={clusterName ? '集群信息修改' : '接入集群'}
      width={700}
      visible={visible}
      destroyOnClose
      onClose={() => handleClose()}
      closeOnOverlayClick={false}
      placement='center'
      footer={
        <Footer>
          <FooterLeft>
            {clusterName && (
              <DisableWrapper>
                <Button theme='danger' variant='outline' onClick={confirmDelete}>
                  删除
                </Button>
              </DisableWrapper>
            )}
          </FooterLeft>
          <Space>
            <Button theme='default' variant='outline' onClick={() => handleClose()}>
              取消
            </Button>
            <DisableWrapper>
              <Button theme='primary' loading={submitting} onClick={() => form.submit()}>
                确定
              </Button>
            </DisableWrapper>
          </Space>
        </Footer>
      }
      {...others}
    >
      {debouceVisible && (
        <AnchroRoot container='#anchor-container' cursor={<Cursor />} onClick={handleClick}>
          <AnchorItem href='#basic' title='基本信息' />
          <AnchorItem href='#cluster' title='集群信息' />
          <AnchorItem href='#Metrics' title='可观测配置' />
        </AnchroRoot>
      )}

      <Form
        form={form}
        onFinish={handleSubmit}
        colon
        labelAlign='right'
        labelWidth='200px'
        initialValues={initial}
      >
        <Body id='anchor-container' className='narrow-scrollbar'>
          <Divider style={{ marginTop: 0 }} align='left' id='basic'>
            基本信息
          </Divider>
          <FormItem
            rules={[{ required: true, message: 'clusterName不能为空' }, formRulePattern('name')]}
            name='clusterName'
            label={<ColTip content='KOP集群设置的clusterName配置'>集群名称</ColTip>}
          >
            <Input disabled={!isVoid(clusterName)} placeholder='请输入' />
          </FormItem>
          <FormItem name='clusterType' label='集群类型' rules={[{ required: true }]}>
            <Select
              placeholder='请选择'
              disabled={!isVoid(clusterName)}
              onChange={clusterTypeChange}
              options={[
                { value: 'kop', label: 'KOP' },
                { value: 'kafka', label: 'KAFKA' },
              ]}
            />
          </FormItem>
          <FormItem name='displayName' label='集群备注'>
            <Input placeholder='请输入' />
          </FormItem>
          <FormItem rules={[{ required: true }]} name='clusterEnv' label='部署环境'>
            <Select placeholder='请选择' options={toTsOptions(clusterEnvs)} />
          </FormItem>
          <TipContainer>
            没找到，点击
            <Link
              theme='primary'
              hover='color'
              size='small'
              target='_blank'
              href={`/maintenance/resource/cluster`}
            >
              这里
            </Link>
            添加
          </TipContainer>
          <FormItem rules={[{ required: true }]} name='deployEnv' label='部署方式'>
            <Select placeholder='请选择' options={toTsOptions(deployEnvs)} />
          </FormItem>
          <FormItem rules={[{ required: true }]} name='clusterUsage' label='分配方式'>
            <Select placeholder='请选择' options={toTsOptions(clusterUsage)} />
          </FormItem>
          <FormItem rules={[{ required: true }]} name='deployVersion' label='版本来源'>
            <Select placeholder='请选择' options={toTsOptions(deployVersions)} />
          </FormItem>

          <FormItem rules={[{ required: true }]} name='app' label='归属应用'>
            <Input placeholder='请输入' />
          </FormItem>

          <FormItem rules={[{ required: true }]} name='principal' label='管理员'>
            <SelectCollapsed
              clearable
              multiple
              filterable
              options={toTsOptions(adminOptions)}
              placeholder='请选择'
            />
          </FormItem>

          <FormItem name='description' label='集群描述'>
            <Textarea placeholder='请输入' />
          </FormItem>

          <Divider align='left' id='cluster'>
            集群信息
          </Divider>

          <FormItem rules={[{ required: true }]} name='clusterVersion' label='Cluster 版本'>
            <Select placeholder='请选择' options={toTsOptions(kafkaVersions)} />
          </FormItem>

          {clusterType === 'kop' && (
            <>
              <FormItem rules={[{ required: true }]} name='bkVersion' label='BK 版本'>
                <Select placeholder='请选择' options={toTsOptions(BKVersions)} />
              </FormItem>
              <FormItem rules={[{ required: true }]} name='zkVersion' label='ZK 版本'>
                <Select placeholder='请选择' options={toTsOptions(ZKVersions)} />
              </FormItem>
            </>
          )}

          <FormItem name='clusterAuthType' label='Cluster 认证类型'>
            <Radio.Group>
              {clusterType === 'kop' && <Radio value='JWT'>JWT</Radio>}
              {clusterType === 'kafka' && <Radio value='SCRAM'>SCRAM</Radio>}

              <Radio value='NONE'>NONE</Radio>
            </Radio.Group>
          </FormItem>

          {clusterAuthType === 'JWT' && (
            <FormItem rules={[{ required: true }]} name='jwt' label='JWT Token'>
              <Textarea placeholder='请输入' />
            </FormItem>
          )}

          {clusterAuthType === 'SCRAM' && (
            <>
              <FormItem rules={[{ required: true }]} name='username' label='用户名'>
                <Input placeholder='请输入' />
              </FormItem>
              <FormItem rules={[{ required: true }]} name='password' label='密码'>
                <Input placeholder='请输入' />
              </FormItem>
            </>
          )}

          <FormItem rules={[{ required: true }]} name='clusterServiceUrl' label='集群服务地址'>
            <ValidateWrapper
              onValidate={async (value) => {
                validateEnv();
                const params = {
                  url: value,
                  type: clusterAuthType,
                  clusterEnv,
                };
                if (clusterType === 'kafka') {
                  params.username = username;
                  params.password = password;
                }

                if (clusterType === 'kop') {
                  params.token = jwt;
                }
                const { data } = await validateKop(params);
                return data;
              }}
            >
              <Input placeholder='请输入' />
            </ValidateWrapper>
          </FormItem>

          {clusterType === 'kop' && (
            <>
              <FormItem
                rules={[{ required: true }]}
                name='brokerServiceUrl'
                label='Broker数据流URL'
              >
                <ValidateWrapper
                  onValidate={async (value) => {
                    validateEnv();
                    const { data } = await validatePulsarClient({
                      url: value,
                      type: clusterAuthType,
                      token: jwt,
                      clusterEnv,
                    });
                    return data;
                  }}
                >
                  <Input placeholder='请输入' />
                </ValidateWrapper>
              </FormItem>

              <FormItem rules={[{ required: true }]} name='brokerAdminUrl' label='Broker管理流URL'>
                <ValidateWrapper
                  onValidate={async (value) => {
                    validateEnv();
                    const { data } = await validatePulsarAdmin({
                      url: value,
                      type: clusterAuthType,
                      token: jwt,
                      clusterEnv,
                    });
                    return data;
                  }}
                >
                  <Input placeholder='请输入' />
                </ValidateWrapper>
              </FormItem>

              <FormItem rules={[{ required: true }]} name='bookieAdminUrl' label='Bookie管理流URL'>
                <ValidateWrapper
                  onValidate={async (value) => {
                    validateEnv();
                    const { data } = await validateBookieAdmin({
                      url: value,
                      clusterEnv,
                    });
                    return data;
                  }}
                >
                  <Input placeholder='请输入' />
                </ValidateWrapper>
              </FormItem>
            </>
          )}

          <Divider align='left' id='Metrics'>
            可观测配置
          </Divider>

          <FormItem
            rules={[{ required: true }]}
            name='prometheusServiceUrl'
            label='Prometheus服务URL'
          >
            <ValidateWrapper
              onValidate={async (value) => {
                validateEnv();
                const { data } = await validatePrometheus({
                  url: value,
                  clusterEnv,
                });
                return data;
              }}
            >
              <Input placeholder='请输入' />
            </ValidateWrapper>
          </FormItem>

          <FormItem name='isOpenGrafanaService' label='Grafana配置'>
            <Switch />
          </FormItem>

          {isOpenGrafanaService && (
            <>
              <FormItem rules={[{ required: true }]} name='metricsServiceUrl' label='服务URL'>
                <ValidateWrapper
                  onValidate={async (value) => {
                    validateEnv();
                    if (!grafanaAuthUser) {
                      MessagePlugin.warning('用户名不可为空');
                      return;
                    }
                    const { data } = await validateGrafana({
                      url: value,
                      username: grafanaAuthUser,
                      password: grafanaAuthPassword,
                      clusterEnv,
                    });
                    return data;
                  }}
                >
                  <Input placeholder='请输入' />
                </ValidateWrapper>
              </FormItem>
              <FormItem rules={[{ required: true }]} name='grafanaAuthUser' label='用户名'>
                <Input placeholder='请输入' />
              </FormItem>
              <FormItem name='grafanaAuthPassword' label='密码'>
                <Input placeholder='请输入' />
              </FormItem>
            </>
          )}
        </Body>
      </Form>
    </Dialog>
  );
}

export default connect(
  (state) => ({
    clusterEnvs: state.kopCluster.clusterEnvs,
    deployEnvs: state.dict.deployEnvs,
    clusterUsage: state.dict.clusterUsage,
    deployVersions: state.dict.deployVersions,
    kafkaVersions: state.dict.kafkaVersions,
    BKVersions: state.dict.BKVersions,
    ZKVersions: state.dict.ZKVersions,
    adminOptions: state.kopCluster.adminOptions,
  }),
  {
    fetchClusterAdminList,
  },
)(LinkClusterModal);

const AnchroRoot = styled(Anchor)`
  width: 125px;
  position: absolute;
  right: 18px;

  background-color: transparent;
  .t-anchor__item-link {
    line-height: 32px;
    color: #8f959f;
  }
`;

const Body = styled.div`
  height: 65vh;
  padding-right: 156px;
  padding-bottom: 30px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Cursor = styled.div`
  width: 10px;
  height: 10px;
  background-color: #0052d9;
  position: absolute;
  border-radius: 50%;
  left: 50%;
  margin-left: -5px;
  top: 50%;
  margin-top: -5px;
`;

const TipContainer = styled.div`
  margin-bottom: 12px;
  margin-left: 200px;
  margin-top: -12px;
  font-size: 12px;
  color: var(--td-text-color-secondary);
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FooterLeft = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
